import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, FileText, MessageSquare, HelpCircle, Network, Image, Palette, Users, Settings, LogOut, Menu, X, ExternalLink, ContactRound, CalendarDays, Eye, BadgeEuro, Inbox, QrCode
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/apiConfig";

const contentNavItems = [
  { to: "/admin/pages", icon: Network, label: "Arborescence" },
  { to: "/admin/articles", icon: FileText, label: "Articles" },
  { to: "/admin/temoignages", icon: MessageSquare, label: "Témoignages" },
  { to: "/admin/faq", icon: HelpCircle, label: "FAQ" },
  { to: "/admin/media", icon: Image, label: "Médiathèque" },
  { to: "/admin/themes", icon: Palette, label: "Thèmes" },
];

const administrationNavItems = [
  { to: "/admin/utilisateurs", icon: Users, label: "Utilisateurs" },
  { to: "/admin/vCard", icon: QrCode, label: "vCard" },
  { to: "/admin/parametres", icon: Settings, label: "Paramètres généraux", settingsSection: "general" },
  { to: "/admin/parametres?section=calendar", icon: CalendarDays, label: "Paramètres du calendrier", settingsSection: "calendar" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [peopleLabel, setPeopleLabel] = useState("Clients");
  const isVisitor = user?.role === "VISITOR";
  const roleLabel = user?.role === "ADMIN" ? "Administrateur" : isVisitor ? "Visiteur" : "Éditeur";
  useEffect(() => { axios.get(`${API}/admin/settings`).then(r => setPeopleLabel(r.data.people_label?.trim() || "Clients")).catch(() => {}); }, []);
  const navGroups = [
    {
      label: "Pilotage",
      items: [
        { to: "/admin", icon: LayoutDashboard, label: "Vue d'ensemble", end: true },
        { to: "/admin/tarifs", icon: BadgeEuro, label: "Prestations & forfaits" },
        { to: "/admin/personnes", icon: ContactRound, label: peopleLabel },
        { to: "/admin/messagerie", icon: Inbox, label: "Messagerie" },
        { to: "/admin?view=month#calendrier", icon: CalendarDays, label: "Rendez-vous", calendar: true },
      ],
    },
    { label: "Contenus", items: contentNavItems },
    { label: "Administration", items: administrationNavItems },
  ];

  const handleLogout = () => { logout(); navigate("/admin/login"); };
  const blockVisitorSave = event => {
    if (!isVisitor) return;
    const button = event.target.closest("button");
    if (!button) return;
    const intent = `${button.textContent || ""} ${button.getAttribute("aria-label") || ""} ${button.dataset.testid || ""}`;
    if (!/(enregistrer|sauvegarder|publier|conserver|save)/i.test(intent)) return;
    event.preventDefault();
    event.stopPropagation();
    toast.error("Mode Visiteur : consultation uniquement. Aucun enregistrement n’est autorisé.", { id: "visitor-read-only" });
  };
  const blockVisitorSubmit = event => {
    if (!isVisitor) return;
    event.preventDefault();
    event.stopPropagation();
    toast.error("Mode Visiteur : consultation uniquement. Aucun enregistrement n’est autorisé.", { id: "visitor-read-only" });
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-sage/10">
        <div className="font-playfair text-xl text-charcoal">Eudemonie</div>
        <div className="font-manrope text-xs text-charcoal-muted mt-0.5">Espace administration</div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
        {navGroups.map(group => (
          <div key={group.label}>
            <p className="px-3 mb-1.5 font-manrope text-[10px] font-medium uppercase tracking-[0.14em] text-charcoal-muted/75">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => {
                    const calendarActive = item.calendar && location.pathname === "/admin" && location.hash === "#calendrier";
                    const dashboardActive = item.end && isActive && location.hash !== "#calendrier";
                    const currentSettingsSection = new URLSearchParams(location.search).get("section") === "calendar" ? "calendar" : "general";
                    const settingsActive = item.settingsSection && location.pathname === "/admin/parametres" && item.settingsSection === currentSettingsSection;
                    const active = item.settingsSection ? settingsActive : item.calendar ? calendarActive : item.end ? dashboardActive : isActive;
                    return `flex items-center gap-2.5 px-3 py-2 rounded-lg font-manrope text-xs font-medium transition-all duration-200 ${active ? "bg-sage text-white shadow-sm" : "text-charcoal-muted hover:bg-sage/10 hover:text-charcoal"}`;
                  }}>
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-sage/10 space-y-2">
        <a href="/" target="_blank" className="flex items-center gap-2.5 px-3 py-2 rounded-lg font-manrope text-xs text-charcoal-muted hover:bg-sage/10 hover:text-charcoal transition-all">
          <ExternalLink size={15} /> Voir le site
        </a>
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-bone border border-sage/10">
          <div className="w-7 h-7 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
            <span className="font-playfair text-xs text-sage">{(user?.prenom || user?.email)?.[0]?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-manrope text-xs font-medium text-charcoal truncate">{user?.prenom || user?.email}</p>
            <p className="font-manrope text-xs text-charcoal-muted">{roleLabel}</p>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="text-charcoal-muted hover:text-red-500 transition-colors">
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bone-light flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 bg-white border-r border-sage/10 flex-col shrink-0 fixed h-full z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-56 bg-white h-full flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:ml-56 flex flex-col">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-sage/10 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-sidebar-toggle" className="p-2 text-charcoal"><Menu size={20} /></button>
          <span className="font-playfair text-lg text-charcoal">Eudemonie Admin</span>
          <div className="w-8" />
        </div>

        {isVisitor && <div role="status" className="sticky top-[57px] z-20 flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-4 py-3 text-center font-manrope text-sm font-semibold text-amber-900 md:top-0"><Eye size={17} className="shrink-0"/><span>Mode Visiteur · consultation uniquement. Aucune modification n’est autorisée dans cet espace, notamment en production.</span></div>}

        {/* Content */}
        <main className="flex-1 p-4 lg:p-5" onClickCapture={blockVisitorSave} onSubmitCapture={blockVisitorSubmit}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
